import { ExclamationCircleIcon } from '@heroicons/react/20/solid';
import { ReactNode, useState } from 'react';

export type TextInputProps = {
    /**
     * Html id for the input.
     */
    id?: string;

    /**
     * Determines what keys are allowed to be entered. Example: a number input only allows numbers.
     */
    type?: 'text' | 'number' | 'date';

    /**
     * An initial value for the input
     */
    defaultValue?: string;

    /**
     * The value of the name attribute on the input element.
     */
    name?: string;

    /**
     * Associate this input with a form.
     */
    form?: string;

    /**
     * The value of the placeholder attribute on the input element.
     */
    placeholder?: string;

    /**
     * The title explaining the use of the input.
     */
    title?: string;

    /**
     * Displays an "optional" hint.
     */
    isOptional?: boolean;

    /**
     * Displays a hint under the component.
     */
    hint?: string;

    /**
     * Max number characters allowed.
     */
    characterLimit?: number;

    /**
     * Icon to show to the left of the text. DO NOT apply styles.
     */
    icon?: ReactNode;

    /**
     * Display an error message under the input in red.
     */
    errorMessage?: string;

    /**
     * Show the input in an error state
     */
    isError?: boolean;

    /**
     * Automatically set the focus of the page to this element
     */
    autoFocus?: boolean;

    /**
     * Aligns the text
     */
    textAlign?: 'left' | 'center' | 'right';

    /**
     * Height of the input
     */
    size?: 'small' | 'regular';

    /**
     * Show a leading dropdown menu.
     */
    dropdownOptions?: { label: string; value: string; isSelected?: boolean }[];

    /**
     * Disable the input of text.
     */
    isDisabled?: boolean;

    /**
     * Callback function invoked when the value is changed
     */
    onChange?: (value: string) => void;

    /**
     * Callback function invoked when a dropdown option is selected
     */
    onDropdownChange?: (value: string) => void;
};

function TextInput({
    type = 'text',
    defaultValue,
    name,
    form,
    placeholder,
    title,
    hint,
    id,
    isOptional,
    icon,
    characterLimit,
    errorMessage,
    autoFocus = false,
    isError = false,
    textAlign = 'left',
    size = 'regular',
    dropdownOptions,
    isDisabled = false,
    onChange,
    onDropdownChange,
}: TextInputProps): JSX.Element {
    const [value, setValue] = useState<string | undefined>(defaultValue);

    /**
     * Style ternaries just to make the className props more readable
     */
    const iconStyles = icon ? 'pl-10' : '';
    const dropdownStyles = dropdownOptions ? 'pl-16' : '';
    const errorStyles =
        errorMessage || isError
            ? 'text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 border-red-300'
            : 'focus:ring-indigo-500 focus:border-indigo-500 border-gray-300';

    function handleInputChange(e: React.ChangeEvent<HTMLInputElement>): void {
        const { value } = e.target;

        let val = characterLimit !== undefined ? value.substring(0, characterLimit) : value;

        if (type === 'number') {
            // val = Math.abs(+val).toString();
            if (+val < 0 || Object.is(+val, -0)) {
                val = (+val * -1).toString();
            }
        }
        setValue(val);
        if (onChange) {
            onChange(val);
        }
    }

    function handleDropdownChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        const { value } = e.target;

        if (onDropdownChange) {
            onDropdownChange(value);
        }
    }

    return (
        <div>
            {/* Title & Optional */}
            <div className="flex justify-between">
                {/* Show a title */}
                {title && <label className="block text-sm font-medium text-gray-700 mb-1">{title}</label>}
                {/* Show an optional message */}
                {isOptional && <span className="text-sm text-gray-500 ml-auto mb-1">Optional</span>}
            </div>

            {/* Input Container */}
            <div className="relative rounded-md shadow-sm">
                {/* Icon */}
                {icon && (
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="h-5 w-5 text-gray-400 mr-10">{icon}</span>
                    </div>
                )}

                {/* Dropdown */}
                {dropdownOptions && (
                    <div className="absolute inset-y-0 left-0 flex items-center">
                        <select
                            disabled={isDisabled}
                            className={`focus:ring-indigo-500 focus:border-indigo-500 h-full py-0 pl-3 pr-7 border-transparent bg-transparent text-gray-500 ${
                                size === 'small' ? 'text-xs rounded' : 'text-sm rounded-md'
                            }`}
                            onChange={handleDropdownChange}
                            defaultValue={dropdownOptions.find((o) => o.isSelected)?.value}
                        >
                            {dropdownOptions.map((option) => {
                                return (
                                    <option value={option.value} key={option.value}>
                                        {option.label}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                )}

                {/* Actual Input Element */}
                <input
                    id={id}
                    autoFocus={autoFocus}
                    type={type}
                    step="any"
                    name={name}
                    className={`${iconStyles} ${errorStyles} ${dropdownStyles} ${
                        size === 'regular' ? 'rounded-md text-sm' : 'rounded h-[34px] text-xs'
                    } ${errorMessage ? 'pr-10' : ''} ${isDisabled ? 'opacity-50' : ''} shadow-sm block w-full `}
                    placeholder={placeholder}
                    value={value || ''}
                    form={form}
                    onChange={handleInputChange}
                    style={{ textAlign }}
                    disabled={isDisabled}
                />

                {/* Error Icon */}
                {errorMessage && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                    </div>
                )}
            </div>

            <div className="flex">
                {/* Hint */}
                {hint && !errorMessage && <p className="mt-2 text-sm text-gray-500">{hint}</p>}
                {/* Error Message */}
                {errorMessage && <p className="mt-2 text-sm text-red-600">{errorMessage}</p>}

                {characterLimit !== undefined && !errorMessage && (
                    <p className="mt-2 text-sm text-gray-500 ml-auto">
                        {value?.length || 0} / {characterLimit}
                    </p>
                )}
            </div>
        </div>
    );
}
export default TextInput;
